/* eslint-disable unicorn/no-null */
import type { Any } from '@livecontrol/core-utils';
import type { VideoJsPlayer } from 'video.js';
import videojs from 'video.js';
import type { StreamQuality } from '../../types';

interface OnFullscreenChangeData {
  isFullscreen: boolean;
}

interface OnPictureInPictureChangeData {
  isPictureInPicture: boolean;
}

interface OnSeekData {
  time: number;
  isLive: boolean;
}

interface OnVolumeChangeData {
  volume: number;
}

interface OnQualityChangeData {
  quality: StreamQuality;
}

interface OnErrorData {
  error: MediaError | null;
}

export interface EventTrackingOptions {
  onFullscreenChange?: (data: OnFullscreenChangeData) => void;
  onPictureInPictureChange?: (data: OnPictureInPictureChangeData) => void;
  onPlay?: () => void;
  onPause?: () => void;
  onSeek?: (data: OnSeekData) => void;
  onEnd?: () => void;
  onReplay?: () => void;
  onVolumeChange?: (data: OnVolumeChangeData) => void;
  onQualityChange?: (data: OnQualityChangeData) => void;
  onError?: (data: OnErrorData) => void;

  /**
   * Volume change timeout (in ms)
   *
   * @default 1000
   */
  volumeChangeTimeout?: number;

  /**
   * Seek timeout (in ms)
   *
   * @default 1000
   */
  seekTimeout?: number;
}

const EVENT_PREFIX = 'event-tracking::';

export const PlayerEvents = {
  FULLSCREEN: `${EVENT_PREFIX}fullscreenchange`,
  PICTURE_IN_PICTURE: `${EVENT_PREFIX}picture-in-picture`,
  PLAY: `${EVENT_PREFIX}play`,
  PAUSE: `${EVENT_PREFIX}pause`,
  SEEK: `${EVENT_PREFIX}seek`,
  REPLAY: `${EVENT_PREFIX}replay`,
  END: `${EVENT_PREFIX}end`,
  VOLUME_CHANGE: `${EVENT_PREFIX}volumechange`,
  QUALITY_CHANGE: `${EVENT_PREFIX}qualitychange`,
  ERROR: `${EVENT_PREFIX}error`
};

export class EventTracking extends videojs.getPlugin('plugin') {
  private readonly options: EventTrackingOptions = {
    volumeChangeTimeout: 1000,
    seekTimeout: 1000
  };

  private volumeChangingTimeout: NodeJS.Timeout | null = null;
  private seekingTimeout: NodeJS.Timeout | null = null;

  private isSeeking = false;
  private isPlaying = false;
  private isPaused = false;
  private isEnded = false;

  private selectedQuality: StreamQuality = 'auto';

  private get liveButton(): HTMLButtonElement {
    return <HTMLButtonElement>this.player.$('.vjs-seek-to-live-control');
  }

  private get isLive(): boolean {
    return (
      this.player.$('.vjs-seek-to-live-control > .vjs-control-text').textContent ===
      'Seek to live, currently playing live'
    );
  }

  private progressControlClicked = false;
  private progressHolderClicked = false;
  private windowClicked = false;

  private get progressControl(): HTMLDivElement {
    return <HTMLDivElement>this.player.$('.vjs-progress-control');
  }

  private get progressHolder(): HTMLDivElement {
    return <HTMLDivElement>this.player.$('.vjs-progress-holder');
  }

  private get mouseClicked(): boolean {
    return this.progressControlClicked || this.progressHolderClicked || this.windowClicked;
  }

  public constructor(player: VideoJsPlayer, options?: EventTrackingOptions) {
    super(player, options);

    this.options = {
      ...this.options,
      ...options
    };

    this.setup();
  }

  private setup(): void {
    this.registerMouseEvents();
    this.registerQualityUpdates();
    this.trackLiveButtonClick();

    this.on(this.player, ['enterpictureinpicture', 'leavepictureinpicture'], (e) => {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      const eventType = <string>e.type;

      const isPictureInPicture = eventType === 'enterpictureinpicture';
      const data: OnPictureInPictureChangeData = { isPictureInPicture };

      this.options.onPictureInPictureChange?.(data);
      this.player.trigger(PlayerEvents.PICTURE_IN_PICTURE, data);
    });

    this.player.on('fullscreenchange', () => {
      const isFullscreen = this.player.isFullscreen();
      const data: OnFullscreenChangeData = { isFullscreen };

      this.options.onFullscreenChange?.(data);
      this.player.trigger(PlayerEvents.FULLSCREEN, data);
    });

    this.player.on('play', () => {
      if (this.isPlaying || this.isSeeking || this.player.seeking()) {
        return;
      }

      this.isPlaying = true;
      this.isPaused = false;

      this.options.onPlay?.();
      this.player.trigger(PlayerEvents.PLAY);
    });

    this.player.on('pause', () => {
      if (this.player.remainingTime() === 0) {
        const bufferedEnd = Number(this.player.bufferedEnd());

        if (!bufferedEnd) {
          // error occurred
          const error = this.player.error();

          const data: OnErrorData = { error };

          this.options.onError?.(data);
          this.player.trigger(PlayerEvents.ERROR, data);

          return;
        }

        this.isEnded = true;
        this.isPaused = true;
        this.isPlaying = false;
        this.options.onEnd?.();
        this.player.trigger(PlayerEvents.END);

        return;
      }

      if (this.isPaused || this.isSeeking || this.player.seeking()) {
        return;
      }

      this.isPaused = true;
      this.isPlaying = false;
      this.options.onPause?.();
      this.player.trigger(PlayerEvents.PAUSE);
    });

    this.player.on('volumechange', () => {
      if (this.volumeChangingTimeout) {
        clearTimeout(this.volumeChangingTimeout);
      }

      this.volumeChangingTimeout = setTimeout(() => {
        const volume = this.player.muted() ? 0 : this.player.volume();

        const data: OnVolumeChangeData = { volume };

        this.options.onVolumeChange?.(data);
        this.player.trigger(PlayerEvents.VOLUME_CHANGE, data);
      }, this.options.volumeChangeTimeout);
    });

    this.player.on('seeking', () => {
      this.isSeeking = true;

      if (this.isEnded && this.player.currentTime() <= 1) {
        // replay
        this.isPlaying = true;
        this.isPaused = false;
        this.isEnded = false;
        this.isSeeking = false;
        this.options.onReplay?.();
        this.player.trigger(PlayerEvents.REPLAY);

        if (this.seekingTimeout) {
          clearTimeout(this.seekingTimeout);
        }

        return;
      }

      if (!this.mouseClicked) {
        // event triggered not by user
        this.isSeeking = false;

        return;
      }

      if (this.seekingTimeout) {
        clearTimeout(this.seekingTimeout);
      }

      this.seekingTimeout = setTimeout(() => {
        this.isSeeking = false;

        const data: OnSeekData = {
          time: this.player.currentTime(),
          isLive: this.isLive
        };

        this.options.onSeek?.(data);
        this.player.trigger(PlayerEvents.SEEK, data);
      }, this.options.seekTimeout);
    });
  }

  private registerQualityUpdates(): void {
    // eslint-disable-next-line
    const qualityLevels = (this.player as Any).qualityLevels?.();

    // eslint-disable-next-line
    qualityLevels?.on('change', ({ selectedIndex }: { selectedIndex: number }) => {
      setTimeout(() => {
        if (selectedIndex === -1) {
          return;
        }

        const settingsSelectorNode = this.player.controlBar
          .el()
          .querySelectorAll('.vjs-http-source-selector')
          .item(0);

        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        if (!settingsSelectorNode) {
          return;
        }

        const qualityItem = settingsSelectorNode
          .querySelectorAll('.vjs-menu-item.vjs-selected')
          .item(0);

        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        if (!qualityItem) {
          this.selectedQuality = 'auto';

          return;
        }

        const quality = qualityItem.firstElementChild?.innerHTML.toLowerCase();

        if (quality !== this.selectedQuality) {
          this.selectedQuality = quality ? <StreamQuality>quality : 'auto';

          const data: OnQualityChangeData = { quality: this.selectedQuality };

          this.options.onQualityChange?.(data);
          this.player.trigger(PlayerEvents.QUALITY_CHANGE, data);
        }
      });
    });
  }

  private registerMouseEvents(): void {
    const onProgressControlPressed = (): void => {
      this.progressControlClicked = true;
    };

    const onProgressHolderPressed = (): void => {
      this.progressHolderClicked = true;
    };

    const onWindowPressed = (): void => {
      this.windowClicked = true;
    };

    const onProgressControlUnpressed = (): void => {
      this.progressControlClicked = false;
    };

    const onProgressHolderUnpressed = (): void => {
      this.progressHolderClicked = false;
    };

    const onWindowUnpressed = (): void => {
      this.windowClicked = false;
    };

    ['mousedown', 'touchstart'].forEach((event) => {
      this.progressControl.addEventListener(event, onProgressControlPressed);
      this.progressHolder.addEventListener(event, onProgressHolderPressed);
      window.addEventListener(event, onWindowPressed);
    });

    ['mouseup', 'touchend'].forEach((event) => {
      this.progressControl.addEventListener(event, onProgressControlUnpressed);
      this.progressHolder.addEventListener(event, onProgressHolderUnpressed);
      window.addEventListener(event, onWindowUnpressed);
    });

    // remove event listeners on dispose
    this.on('dispose', () => {
      ['mousedown', 'touchstart'].forEach((event) => {
        this.progressControl.removeEventListener(event, onProgressControlPressed);
        this.progressHolder.removeEventListener(event, onProgressHolderPressed);
        window.removeEventListener(event, onWindowPressed);
      });

      ['mouseup', 'touchend'].forEach((event) => {
        this.progressControl.removeEventListener(event, onProgressControlUnpressed);
        this.progressHolder.removeEventListener(event, onProgressHolderUnpressed);
        window.removeEventListener(event, onWindowUnpressed);
      });
    });
  }

  private trackLiveButtonClick(): void {
    const onClickCallback = (): void => {
      if (this.isLive) {
        return;
      }

      const data: OnSeekData = {
        time: this.player.currentTime(),
        isLive: true
      };

      this.isPlaying = true;
      this.isPaused = false;
      this.isEnded = false;
      this.isSeeking = false;

      this.options.onSeek?.(data);
      this.player.trigger(PlayerEvents.SEEK, data);
    };

    this.liveButton.addEventListener('click', onClickCallback);
    this.liveButton.addEventListener('touchend', onClickCallback);

    this.on('dispose', () => {
      this.liveButton.removeEventListener('click', onClickCallback);
      this.liveButton.removeEventListener('touchend', onClickCallback);
    });
  }
}
